import { Divider, Grid, Paper, Typography } from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbsData } from '../../Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import styles from '../../agentVerification/agentVerificationDetails/AgentVerificationDetails.module.scss'
import { AgentVerificationDetailsListItem } from '../../agentVerification/agentVerificationDetails/components/agentVerificationDetailsListItem/AgentVerificationDetailsListItem'
import { ActivityCheckSuccessModal } from '../ActivityCheckDialog/ActivityCheckSuccessModal'

import { ActivityCheckById, ActivityCheckStatus } from '@percent/admin-dashboard/api/types'
import { CellStatus, ErrorView, Loader, PercentButton } from '@percent/admin-dashboard/common/components'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { orgSubActivities } from '@percent/admin-dashboard/constants/organisationActivities'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ActivityCheckModalState } from './ActivityCheckDetail.types'
import { ActivityCheckSearchDropdown } from './ActivityCheckSearchDropdown'
import { ActivityCheckSubmitCard } from './ActivityCheckSubmitCard'
import { useQuery } from '@percent/utility'
import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'

const getFormattedActivityData = (data: ActivityCheckById | null) => {
  if (!data) return null

  const isActivityCheckLive = data.status === ActivityCheckStatus.LIVE

  // NOTE: organisationActivity always exists whenever activity check status is "live"
  const currentActivityCode = isActivityCheckLive ? data.organisationActivity!.activity : data.activity

  const orgSubActivity = orgSubActivities[currentActivityCode]
  const primaryDescription = `${orgSubActivity.primaryActivityCode} - ${orgSubActivity.primaryActivityDescription}`
  const subDescription = `${orgSubActivity.subActivityCode} - ${orgSubActivity.subActivityDescription}`

  return {
    isActivityCheckLive,
    definition: orgSubActivity.definition,
    primaryDescription,
    subDescription
  }
}

export function ActivityCheckDetailView() {
  const { id } = useRequiredParams()
  const { t } = useTranslation()
  const { activityCheckService, adminService } = useServices()
  const { setPath } = useBreadcrumbsData()

  const [isActivityCheckModalOpened, setIsActivityCheckModalOpened] = useState<boolean | undefined>(false)
  const [modalState, setModalState] = useState<ActivityCheckModalState>('confirm')
  const [updatedCode, setUpdatedCode] = useState({
    primaryDescription: '',
    subDescription: '',
    definition: ''
  })

  const {
    data,
    isPending,
    errorMessage,
    refetch: refresh
  } = useQuery(['/v1/admin/activity-checks', id], activityCheckService.getActivityCheck, { id })

  const {
    data: orgData,
    isPending: orgLoader,
    errorMessage: orgError
  } = useQuery(
    [`/v1/admin/organisations`, data?.organisationId],
    adminService.getOrganisation,
    { id: data?.organisationId },
    { enabled: !!data?.organisationId }
  )

  const formattedData = useMemo(() => getFormattedActivityData(data), [data])

  useEffect(() => {
    if (formattedData) setUpdatedCode(formattedData)
  }, [formattedData])

  useEffect(() => {
    if (data) setPath(`${data.status}`)

    return () => setPath(undefined)
  }, [setPath, data])

  if (errorMessage !== null || orgError !== null) {
    return <ErrorView errorMessage={(errorMessage || orgError) as string} />
  }

  if (isPending || orgLoader || !formattedData) {
    return <Loader />
  }

  const { isActivityCheckLive, primaryDescription, subDescription } = formattedData

  return (
    <>
      <ActivityCheckSubmitCard
        setIsOpened={setIsActivityCheckModalOpened}
        orgName={orgData?.name}
        orgWebsite={orgData?.website}
        orgId={data.organisationId}
      />
      <Grid container spacing={0} component={Paper} elevation={0} className={styles.generalInfoWrapper}>
        <span className={styles.text}>{t('typography.status')}</span>
        {data?.status && <CellStatus status={data?.status} />}
        <Divider orientation="vertical" flexItem className={styles.divider} />
        <span className={styles.text}>{t('typography.requestDate')}</span>
        <span className={styles.subtext}>{getDate(data?.createdAt)}</span>
        {data.organisationActivity && (
          <>
            <Divider orientation="vertical" flexItem className={styles.divider} />
            <span className={styles.text}>{t('table.reviewedBy')}</span>
            <span className={styles.subtext}>{data.organisationActivity.email}</span>
          </>
        )}
      </Grid>
      <Grid container spacing={0} component={Paper} elevation={0} className={styles.listContainer}>
        <Grid item xs={12} className={styles.missionTitle}>
          <Typography variant="subtitle2">{t('typography.missionStatement')}</Typography>
          <Typography variant="subtitle1" className={styles.titleSubText}>
            {data?.missionStatement}
          </Typography>
        </Grid>
        <ul className={styles.detailsList}>
          <AgentVerificationDetailsListItem
            title={t(isActivityCheckLive ? 'typography.currentCodes' : 'typography.submittedCodes')}
          />
          <AgentVerificationDetailsListItem title={t('typography.primary')}>
            <PercentButton testId="primary-description" title={primaryDescription} disabled />
          </AgentVerificationDetailsListItem>
          <AgentVerificationDetailsListItem title={t('typography.sub')}>
            <PercentButton testId="sub-description" title={subDescription} disabled />
          </AgentVerificationDetailsListItem>
          <li className={styles.searchActivityCheck}>
            <Typography variant="subtitle2">{t('typography.code')}</Typography>
            <ActivityCheckSearchDropdown updatedCode={updatedCode} setUpdatedCode={setUpdatedCode} />
          </li>
        </ul>
      </Grid>
      {isActivityCheckModalOpened && (
        <ActivityCheckSuccessModal
          isOpened={isActivityCheckModalOpened}
          setIsOpened={setIsActivityCheckModalOpened}
          primaryDescription={primaryDescription}
          subDescription={subDescription}
          updatedCode={updatedCode}
          orgName={orgData?.name}
          orgId={data.organisationId}
          refresh={refresh}
          modalState={modalState}
          setModalState={setModalState}
        />
      )}
    </>
  )
}
